import { useFacilityAddOns, useSetFacilityAddOn } from '../index.js'
import { useMe } from '../../accounts/index.js'
import type { EntitlementReason, FacilityAddOn } from '../index.js'
import { HelpIcon, Switch, Skeleton } from '../../core/index.js'
import { EndpointHint } from '../../core/index.js'

const KIND_LABELS = { paid: 'Paid', trial: 'Trial', demo: 'Demo' } as const

const REASON_TEXT: Record<EntitlementReason, string> = {
  enabled: 'On for this facility.',
  not_licensed: 'Not part of your agreement. Ask autonoME to add it.',
  license_not_started: 'Licensed from a later date.',
  license_suspended: 'The license is suspended; ask autonoME.',
  license_ended: 'The license has ended; ask autonoME to renew it.',
  not_activated: 'Switched off for this facility.',
  facility_limit: 'Switched on, but over the number of facilities the license allows. Switch another facility off first.',
}

function formatDate(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/** "Paid license until 30 Sep 2027 · 3 of 5 facilities on" */
function licenseLine(addOn: FacilityAddOn): string {
  if (!addOn.license) {
    return 'Not licensed'
  }
  const parts = [`${KIND_LABELS[addOn.license.kind]} license`]
  if (addOn.license.status !== 'active') {
    parts.push(addOn.license.status)
  }
  if (addOn.license.termEnd) {
    parts.push(`until ${formatDate(addOn.license.termEnd)}`)
  }
  const on = addOn.license.facilityLimit === null ? `${addOn.facilitiesOn} ${addOn.facilitiesOn === 1 ? 'facility' : 'facilities'} on` : `${addOn.facilitiesOn} of ${addOn.license.facilityLimit} facilities on`
  return `${parts.join(' ')} · ${on}`
}

interface FacilityAddOnsCardProps {
  facilityId: string
  /** True for an operator: the switch can be changed. A facility administrator only sees it. */
  canToggle: boolean
}

/**
 * The Configuration page's Add-ons tab: every add-on in the catalog, what
 * the client's agreement says about it, and this facility's switch
 * (docs/plans/feature-licensing-plan.md section 5).
 */
export function FacilityAddOnsCard({ facilityId, canToggle }: FacilityAddOnsCardProps) {
  const me = useMe()
  const addOns = useFacilityAddOns(facilityId)
  const setAddOn = useSetFacilityAddOn(me.data?.clientId, facilityId)

  return (
    <div className="bg-white border border-marine/15 rounded p-5">
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <div className="text-sm font-black text-ink">Add-ons<HelpIcon topic="facilities.config-add-ons" className="ml-1" /></div>
        <EndpointHint>GET /facilities/{'{facilityId}'}/add-ons</EndpointHint>
      </div>
      <p className="text-xs text-ink-mid mb-3">
        Features your agreement with SeeWhoCares includes beyond the core. {canToggle ? 'Switch each on or off for this facility; usage is billed for the facilities that are on.' : 'Your operator switches them on per facility.'}
      </p>
      {addOns.isPending ? (
        <Skeleton />
      ) : addOns.isError ? (
        <div role="alert" className="text-xs font-bold text-coral">
          {addOns.error.message}
        </div>
      ) : addOns.data.length === 0 ? (
        <div className="text-xs text-ink-mid">No add-ons are offered yet.</div>
      ) : (
        <ul className="divide-y divide-marine/10">
          {addOns.data.map((addOn) => {
            const canSwitchOn = addOn.license !== null && (addOn.reason === 'not_activated' || addOn.enabled || addOn.reason === 'facility_limit')
            return (
              <li key={addOn.key} className="py-3 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-ink">{addOn.name}</span>
                    {!addOn.released && <span className="text-[9px] font-bold uppercase tracking-wide bg-silver-lt text-silver px-1.5 py-0.5 rounded">Not released yet</span>}
                  </div>
                  <div className="text-[11px] text-ink-mid mt-0.5">{addOn.description}</div>
                  <div className="text-[11px] text-ink mt-1">{licenseLine(addOn)}</div>
                  <div className={`text-[11px] mt-0.5 ${addOn.enabled ? 'text-green' : 'text-ink-mid'}`}>{REASON_TEXT[addOn.reason]}</div>
                  {addOn.changedAt && addOn.changedBy && (
                    <div className="text-[10px] text-ink-mid mt-0.5">
                      Last changed {formatDate(addOn.changedAt)} by {addOn.changedBy.name ?? addOn.changedBy.sub}
                      {addOn.changedBy.demoSessionOpenedBy ? ' (demo session)' : ''}
                    </div>
                  )}
                </div>
                {canToggle && (
                  <Switch
                    label={`${addOn.name} for this facility`}
                    checked={addOn.switchedOn}
                    disabled={setAddOn.isPending || (!addOn.switchedOn && !canSwitchOn)}
                    onChange={(enabled) => setAddOn.mutate({ key: addOn.key, enabled })}
                  />
                )}
              </li>
            )
          })}
        </ul>
      )}
      {setAddOn.isError && (
        <p role="alert" className="text-[11px] font-bold text-coral mt-2">
          {setAddOn.error.message}
        </p>
      )}
    </div>
  )
}
